// @ts-nocheck
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import dynamic from 'next/dynamic';
import SuperTokens from 'supertokens-auth-react';
import { redirectToAuth } from 'supertokens-auth-react';
import { EmailPasswordComponentsOverrideProvider } from 'supertokens-auth-react/recipe/emailpassword';
import icon from '../../src/assets/icons/logo.svg';

import colors from '../../src/constants/colors';
import Text from 'components/core/Text';
import { appInfo } from '../../config/appInfo';

const SuperTokensComponentNoSSR = dynamic(
  new Promise(res => res(SuperTokens.getRoutingComponent)) as any,
  { ssr: false },
);

const StyledAuthWrapper = styled.div`
  margin: 0;
  padding: 0;
  background: url('/static/images/auth_bg.jpg');
  background-size: cover;
  background-position: center;
  height: 100vh;
  display: flex;
  overflow: hidden;
  align-items: center;
  justify-content: center;

  [data-supertokens~='container'] {
    font-family: inherit;
    font-size: 16px;
  }

  [data-supertokens~='superTokensBranding'],
  [data-supertokens~='headerTitle'] {
    display: none;
  }

  [data-supertokens~='resetPasswordHeaderTitle'] {
    display: block;
    font-weight: 400;

    [data-supertokens~='backButton'] {
      margin-right: 12px;
    }
  }

  /* Hide the default SuperTokens "Forgot password?" link so we can render it in the footer */
  [data-supertokens~='forgotPasswordLink'] {
    display: none;
  }

  /* Remove card styling on reset password page — the wrapper handles it */
  [data-supertokens~='container']:has([data-supertokens~='resetPasswordHeaderTitle']) {
    border-radius: 0;
    box-shadow: none;
    margin: 0;
    padding-bottom: 0;
  }

  [data-supertokens~='button'] {
    background: linear-gradient(to right, ${colors.main.darkBlue}, ${colors.main.lightBlue});
    border: none;
    border-radius: 4px;
    height: 48px;
  }

  [data-supertokens~='input'] {
    font-size: 16px;
  }

  [data-supertokens~='backButton'] {
    svg {
      display: block;
    }
  }
`;

const StyledLogo = styled.div`
  display: block;
  width: 120px;
  height: 80px;
  color: transparent;
  background: url(${icon}) 50% 50% no-repeat;
  background-size: contain;
  background-position: center;
  border: 0;
  outline: 0;
  margin: 0 auto 24px;
`;

const StyledPasswordReset = styled.div`
  margin: 0 auto;
  max-width: 380px;
  width: 100%;
  text-align: center;
`;

const StyledResetCardWrapper = styled.div`
  width: 420px;
  margin: 0 auto;
  border-radius: 8px;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  /* Ensure the SuperTokens container fills the wrapper with no gaps */
  > * {
    flex-shrink: 0;
  }

  @media (max-width: 440px) {
    width: 95vw;
  }
`;

const StyledResetFooter = styled.div`
  background: rgb(255, 255, 255);
  padding: 0 50px 24px;
  text-align: center;
`;

const StyledAuthLink = styled.div`
  margin-top: 16px;
  text-align: center;
  font-size: 14px;
  color: ${colors.main.grey};

  a {
    color: ${colors.main.lightBlue};
    text-decoration: none;
    font-weight: 500;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
      text-decoration: underline;
    }
  }
`;

const StyledDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0 16px;
  color: ${colors.main.grey};
  font-size: 13px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${colors.main.dividers};
  }

  span {
    padding: 0 12px;
  }
`;

const StyledMagicLinkButton = styled.button`
  width: 100%;
  height: 48px;
  border: 2px solid ${colors.main.lightBlue};
  border-radius: 4px;
  background: transparent;
  color: ${colors.main.lightBlue};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${colors.main.lightBlue};
    color: ${colors.main.white};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StyledMagicLinkSuccess = styled.div`
  text-align: center;
  padding: 12px 16px;
  margin-top: 20px;
  background: rgba(120, 208, 128, 0.1);
  border: 1px solid ${colors.main.green};
  border-radius: 4px;
  color: ${colors.main.black};
  font-size: 14px;
`;

const StyledMagicLinkError = styled.div`
  text-align: center;
  margin-top: 8px;
  font-size: 13px;
  color: ${colors.main.ruby};
`;

function getEmailFromForm(): string {
  const input = document.querySelector('[data-supertokens~="input"][name="email"]') as HTMLInputElement;
  return input?.value?.trim() ?? '';
}

function MagicLinkButton({ label }: { label: string }) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleClick = async () => {
    const email = getEmailFromForm();
    if (!email) {
      setStatus('error');
      setErrorMsg('Please enter your email address above first.');
      return;
    }

    setStatus('sending');
    setErrorMsg('');
    try {
      const response = await fetch(`${appInfo.apiDomain}/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        setStatus('sent');
      } else {
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  if (status === 'sent') {
    return (
      <StyledMagicLinkSuccess>
        Check your email for a sign-in link. It expires in 15 minutes.
      </StyledMagicLinkSuccess>
    );
  }

  return (
    <>
      <StyledDivider>
        <span>or</span>
      </StyledDivider>
      <StyledMagicLinkButton type="button" onClick={handleClick} disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending...' : label}
      </StyledMagicLinkButton>
      {status === 'error' && <StyledMagicLinkError>{errorMsg}</StyledMagicLinkError>}
    </>
  );
}

export default function Auth() {
  // if the user visits a page that is not handled by us (like /auth/random), then we redirect them back to the auth page.
  useEffect(() => {
    if (SuperTokens.canHandleRoute() === false) {
      redirectToAuth();
    }
  }, []);

  const componentOverrides = {
    EmailPasswordSignUpHeader_Override: ({ DefaultComponent, ...props }: any) => {
      // @ts-ignore - Third-party override types are incompatible with React 18 typings
      return (
        <>
          <StyledLogo />
          <Text variant="displayM" weight="normal" mt={5} mb={4}>
            Welcome to Praxis
          </Text>
          <Text variant="textSmall" mb={5}>
            👇 Sign up & get moving.
          </Text>
          {/* @ts-ignore - Third-party override types are incompatible with React 18 typings */}
          <DefaultComponent {...props} />
        </>
      ) as any;
    },
    EmailPasswordSignUpFooter_Override: ({ DefaultComponent, ...props }: any) => {
      // @ts-ignore - Third-party override types are incompatible with React 18 typings
      return (
        <>
          <MagicLinkButton label="Email me a sign-up link" />
          {/* @ts-ignore - Third-party override types are incompatible with React 18 typings */}
          <DefaultComponent {...props} />
          <StyledAuthLink>
            Already have an account? <a href="/auth?show=signin">Sign in</a>
          </StyledAuthLink>
        </>
      ) as any;
    },
    EmailPasswordSignInHeader_Override: ({ DefaultComponent, ...props }: any) => {
      // @ts-ignore - Third-party override types are incompatible with React 18 typings
      return (
        <>
          <StyledLogo />
          <Text variant="displayM" weight="normal" mt={5} mb={4}>
            Welcome to Praxis
          </Text>
          <Text variant="textSmall" mb={5}>
            ✊ Log in and let's get to work...
          </Text>
          {/* @ts-ignore - Third-party override types are incompatible with React 18 typings */}
          <DefaultComponent {...props} />
        </>
      ) as any;
    },
    EmailPasswordSignInFooter_Override: ({ DefaultComponent, ...props }: any) => {
      // @ts-ignore - Third-party override types are incompatible with React 18 typings
      return (
        <>
          <MagicLinkButton label="Email me a sign-in link" />
          {/* @ts-ignore - Third-party override types are incompatible with React 18 typings */}
          <DefaultComponent {...props} />
          <StyledAuthLink>
            <a href="/auth/reset-password">Forgot password?</a>
          </StyledAuthLink>
          <StyledAuthLink>
            Don't have an account? <a href="/auth?show=signup">Sign up</a>
          </StyledAuthLink>
        </>
      ) as any;
    },
    EmailPasswordResetPasswordEmail_Override: ({ DefaultComponent, ...props }: any) => {
      // @ts-ignore - Third-party override types are incompatible with React 18 typings
      return (
        <>
          <StyledPasswordReset>
            <StyledLogo />
            <Text variant="textSmall" mb={5}>
              📝 Didn't receive a reset email? You might need to
              <a href="/auth?show=signup" style={{ color: colors.main.lightBlue }}>
                {' '}
                sign up
              </a>
              .
            </Text>
          </StyledPasswordReset>
          <StyledResetCardWrapper>
            {/* @ts-ignore - Third-party override types are incompatible with React 18 typings */}
            <DefaultComponent {...props} />
            <StyledResetFooter>
              <MagicLinkButton label="Skip reset, email me a sign-in link" />
              <StyledAuthLink>
                Remember your password? <a href="/auth?show=signin">Sign in</a>
              </StyledAuthLink>
            </StyledResetFooter>
          </StyledResetCardWrapper>
        </>
      ) as any;
    },
  };

  return (
    <StyledAuthWrapper>
      <EmailPasswordComponentsOverrideProvider components={componentOverrides as any}>
        <SuperTokensComponentNoSSR />
      </EmailPasswordComponentsOverrideProvider>
    </StyledAuthWrapper>
  );
}
